import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormStore } from "../store/formStore";
import { useUiStore } from "../store/uiStore";
import { useAuth } from "../auth/AuthProvider";
import { STEP_LABELS } from "../types";
import { validateStep } from "../validation";
import { useUserRole, type useStepValidation } from "../hooks";
import { saveCurrentDraft } from "../services/saveDraft";
import { releaseLockThen } from "../services/releaseLockThen";
import { Info, LogOut, ArrowLeftRight, ChevronDown, User } from "lucide-react";
import { InfoDrawer } from "./InfoDrawer";

function MenuDropdown({ onSwitchGroup, onLogout, mobile }: { onSwitchGroup: () => void; onLogout: () => void; mobile?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const btnSize = mobile ? "h-8 px-2" : "h-9 px-3";
  const iconSize = mobile ? 14 : 16;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`text-xs font-medium text-white border border-white/30 hover:bg-white/10 ${btnSize} rounded-md transition-colors cursor-pointer flex items-center gap-1.5`}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Account menu"
      >
        <User size={iconSize} aria-hidden="true" />
        <ChevronDown size={mobile ? 10 : 12} aria-hidden="true" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          <button
            onClick={() => { setOpen(false); onSwitchGroup(); }}
            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 cursor-pointer"
          >
            <ArrowLeftRight size={15} className="text-gray-400" aria-hidden="true" />
            Switch Group
          </button>
          <hr className="my-1 border-gray-100" />
          <button
            onClick={() => { setOpen(false); onLogout(); }}
            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 cursor-pointer"
          >
            <LogOut size={15} className="text-gray-400" aria-hidden="true" />
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

interface HeaderProps {
  validation: ReturnType<typeof useStepValidation>;
  /** Read-only review mode (submitted form, or external user on a correction) — locks step navigation. */
  reviewMode?: boolean;
}

export function Header({ validation, reviewMode = false }: HeaderProps) {
  const currentStep = useFormStore((s) => s.currentStep);
  const visitedSteps = useFormStore((s) => s.visitedSteps);
  const data = useFormStore((s) => s.data);
  const saveStatus = useFormStore((s) => s.saveStatus);
  const setStep = useFormStore((s) => s.setStep);
  const isSubmitting = useUiStore((s) => s.isSubmitting);
  const { logout, user } = useAuth();

  const isSaving = saveStatus === "saving";
  const navDisabled = isSubmitting || isSaving || reviewMode;
  const { isExternal } = useUserRole();

  // Validate all visited steps — memoized so it only recomputes when data or visited steps change
  const stepValidity = useMemo(() => {
    const result: Record<number, boolean> = {};
    for (let s = 1; s <= STEP_LABELS.length; s++) {
      if (visitedSteps.has(s)) {
        result[s] = validateStep(s, data).isValid;
      }
    }
    return result;
  }, [data, visitedSteps]);

  // Validate current step before allowing navigation to another step.
  // If current step has errors, show a warning toast but still allow navigation.
  const handleStepClick = useCallback(async (targetStep: number) => {
    if (reviewMode || targetStep === currentStep) return;

    const result = validateStep(currentStep, data);

    const saveThenNavigate = async () => {
      const ok = await saveCurrentDraft();
      if (!ok) return;
      setStep(targetStep);
    };

    // Dev-only override: when the validation toast is disabled, jump directly
    // to the target step without surfacing the gate.
    if (!result.isValid && useUiStore.getState().showValidationToast) {
      validation.touchAll(currentStep);
      useUiStore.getState().showValidationModal(() => { void saveThenNavigate(); });
      useUiStore.setState((s) => ({ validationTouchCounter: s.validationTouchCounter + 1 }));
      return;
    }

    await saveThenNavigate();
  }, [currentStep, data, reviewMode, setStep, validation]);

  // Both exits release the lock BEFORE navigating (see releaseLockThen for why
  // the beforeunload beacon isn't enough). Switch Group must hand the lock over
  // just like Log Out — a pending requester gets it via transfer-on-release
  // instead of waiting out the 30s auto-grant / 90s TTL (ADO-3451).
  const releaseLockAndLogout = useCallback(() => releaseLockThen(logout), [logout]);
  const releaseLockAndSwitchGroup = useCallback(
    () => releaseLockThen(() => { window.location.href = "/"; }),
    [],
  );

  const [infoDrawerOpen, setInfoDrawerOpen] = useState(false);
  const isWelcomeStep = currentStep === 0;

  const progressPct = reviewMode ? 100 : isWelcomeStep ? 0 : ((currentStep - 1) / (STEP_LABELS.length - 1)) * 100;

  return (
    <header className="bg-white border-b border-gray-200">
      {/* Top bar — desktop */}
      <div className="px-6 py-2 hidden md:flex items-center justify-between bg-brand-navy">
        <img src="/horizontal-optimyl-logo-white-text.svg" alt="Optimyl Benefits" className="h-12 w-auto shrink-0" />
        <div className="flex items-center gap-3">
          {user && (
            <span className="text-xs text-white/60 hidden sm:inline">
              {user.profile.email ?? user.profile.name ?? ""}
            </span>
          )}
          {!isWelcomeStep && (
            <button
              onClick={() => setInfoDrawerOpen(true)}
              className="text-xs font-medium text-white border border-white/30 hover:bg-white/10 h-9 px-2.5 rounded-md transition-colors cursor-pointer flex items-center"
              title="Instructions & Info"
              aria-label="Open instructions and info"
            >
              <Info size={17} aria-hidden="true" />
            </button>
          )}
          {isExternal ? (
            <MenuDropdown onSwitchGroup={() => { void releaseLockAndSwitchGroup(); }} onLogout={() => { void releaseLockAndLogout(); }} />
          ) : (
            <button
              onClick={() => { void releaseLockAndLogout(); }}
              className="text-xs font-medium text-white border border-white/30 hover:bg-white/10 h-9 px-3 rounded-md transition-colors cursor-pointer flex items-center gap-1.5"
            >
              <LogOut size={17} aria-hidden="true" />
              Log out
            </button>
          )}
        </div>
      </div>

      {/* Top bar — mobile */}
      <div className="md:hidden px-2 py-2 flex items-center justify-between bg-brand-navy">
        <span className="flex items-center gap-2">
          <img src="/optimyl-icon.svg" alt="" className="h-8 w-8 shrink-0" aria-hidden="true" />
          <span className="text-lg font-semibold text-white tracking-tight">Optimyl Benefits</span>
        </span>
        <div className="flex items-center gap-2">
          {!isWelcomeStep && (
            <button
              onClick={() => setInfoDrawerOpen(true)}
              className="text-xs font-medium text-white border border-white/30 hover:bg-white/10 h-8 px-2 rounded-md transition-colors cursor-pointer flex items-center"
              title="Instructions & Info"
              aria-label="Open instructions and info"
            >
              <Info size={16} aria-hidden="true" />
            </button>
          )}
          {isExternal ? (
            <MenuDropdown onSwitchGroup={() => { void releaseLockAndSwitchGroup(); }} onLogout={() => { void releaseLockAndLogout(); }} mobile />
          ) : (
            <button
              onClick={() => { void releaseLockAndLogout(); }}
              className="text-xs font-medium text-white border border-white/30 hover:bg-white/10 h-8 px-2.5 rounded-md transition-colors cursor-pointer flex items-center gap-1.5"
              aria-label="Log out"
              title="Log out"
            >
              <LogOut size={16} aria-hidden="true" />
              Log out
            </button>
          )}
        </div>
      </div>

      {/* Step progress — desktop (hidden on welcome and thank you pages) */}
      {!isWelcomeStep && !reviewMode && <nav className="max-w-5xl mx-auto px-6 pb-3 hidden md:block relative mt-4" aria-label="Form progress">
        {/* Track line */}
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${progressPct}%` }} />
        </div>

        {/* Step dots */}
        <ol className="flex items-center justify-between relative z-10">
          {STEP_LABELS.map((label, i) => {
            const stepNum = i + 1;
            const isCurrent = stepNum === currentStep && !reviewMode;
            const isVisited = visitedSteps.has(stepNum);
            const isValid = stepValidity[stepNum];
            const isComplete = isVisited && !isCurrent && isValid;
            const isIncomplete = isVisited && !isCurrent && isValid === false;

            return (
              <li key={label} className="flex flex-col items-center flex-1">
                <div className="relative">
                  <button
                    onClick={() => { void handleStepClick(stepNum); }}
                    disabled={navDisabled}
                    className={`
                      w-9 h-9 rounded-full text-xs font-semibold flex items-center justify-center
                      transition-all duration-200 cursor-pointer
                      ${isCurrent
                        ? "bg-brand-green text-white progress-dot--active"
                        : isComplete
                          ? "bg-brand-green text-white"
                          : isIncomplete
                            ? "bg-brand-green text-white"
                            : isVisited
                              ? "bg-brand-green-light text-brand-green-dark border border-brand-green"
                              : "bg-gray-200 text-gray-400"
                      }
                      hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                    aria-current={isCurrent ? "step" : undefined}
                    aria-label={`Step ${stepNum}: ${label}${isIncomplete ? " (incomplete)" : ""}`}
                  >
                    {isComplete ? "\u2713" : stepNum}
                  </button>
                  {isIncomplete && (
                    <span className="progress-badge" aria-hidden="true" style={{ fontSize: 0, lineHeight: 0 }}>
                      <svg width="22" height="20" viewBox="0 0 16 14" fill="none">
                        <path d="M7.134 1.5a1 1 0 011.732 0l5.856 10.133a1 1 0 01-.866 1.5H2.144a1 1 0 01-.866-1.5L7.134 1.5z" className="fill-warning"/>
                        <text x="8" y="11" textAnchor="middle" className="fill-white" fontSize="9" fontWeight="bold" fontFamily="sans-serif">!</text>
                      </svg>
                    </span>
                  )}
                </div>
                <span
                  className={`text-[9px] uppercase tracking-wider mt-2
                    ${isCurrent
                      ? "font-semibold text-brand-green-dark"
                      : isComplete || isIncomplete || isVisited
                        ? "text-brand-green-dark"
                        : "text-gray-400"
                    }
                  `}
                >
                  {label}
                </span>
              </li>
            );
          })}
        </ol>
      </nav>}

      {/* Step progress — mobile (hidden on welcome and thank you pages) */}
      {!isWelcomeStep && !reviewMode && (
        <div className="md:hidden px-4 pb-3">
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
              Step {currentStep} of {STEP_LABELS.length} — {Math.round(progressPct)}%
            </span>
            <span className="text-sm font-bold text-brand-navy">
              {STEP_LABELS[currentStep - 1]}
            </span>
          </div>
          <div
            className="h-1 bg-gray-200 rounded-full overflow-hidden"
            role="progressbar"
            aria-valuenow={Math.round(progressPct)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Form progress: step ${currentStep} of ${STEP_LABELS.length}`}
          >
            <div
              className="h-full bg-brand-green rounded-full transition-all duration-[400ms]"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      )}

      <InfoDrawer open={infoDrawerOpen} onClose={() => setInfoDrawerOpen(false)} />
    </header>
  );
}
